import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { customersApi, installationsApi, inventoryApi, staffApi } from '../api/client';
import { STATES, getDistricts, DEFAULT_STATE, DEFAULT_DISTRICT } from '../data/indiaLocations';
import { useAuth } from '../context/AuthContext';
import { Modal, Badge, CID, Spin, Empty, useFlash, F, Confirm, parseErr } from '../components/UI';
import { Trash } from '../components/Icons';

const BLANK_EXISTING = { product_id:'', product_name:'', sale_date:'', technician:'' };

export default function Customers() {
  const { canDelete, canWrite } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]); const [busy, setBusy] = useState(true);
  const [q, setQ] = useState(''); const [showAdd, setAdd] = useState(false); const [del, setDel] = useState(null);
  const { show, Flash } = useFlash();
  const [form, setForm] = useState({ salutation:'',full_name:'',address:'',district:DEFAULT_DISTRICT,state:DEFAULT_STATE,pincode:'',phone:'',email:'' });
  const [saving, setSaving] = useState(false); const [err, setErr] = useState('');
  const set = k => e => setForm(f=>({...f,[k]:e.target.value}));

  // Existing customer extras
  const [isExisting, setIsExisting]     = useState(false);
  const [existingForm, setExistingForm] = useState(BLANK_EXISTING);
  const [staff, setStaff]               = useState([]);
  const [prods, setProds]               = useState([]);
  const setEF = k => e => setExistingForm(f => ({ ...f, [k]: e.target.value }));
  const handleProdSelect = e => {
    const p = prods.find(p => p.id === e.target.value);
    setExistingForm(f => ({ ...f, product_id: e.target.value, product_name: p ? p.name : f.product_name, warranty_months: p?.warranty_months || f.warranty_months || 12 }));
  };

  const load = useCallback(async (search='') => {
    setBusy(true);
    try { const { data } = await customersApi.list(search?{q:search}:{}); setRows(data); }
    finally { setBusy(false); }
  }, []);

  useEffect(() => {
    load();
    staffApi.list().then(({ data }) => setStaff(data)).catch(() => {});
    inventoryApi.list().then(({ data }) => setProds(data)).catch(() => {});
  }, [load]);

  const resetModal = () => {
    setForm({ salutation:'',full_name:'',address:'',district:DEFAULT_DISTRICT,state:DEFAULT_STATE,pincode:'',phone:'',email:'' });
    setIsExisting(false); setExistingForm(BLANK_EXISTING); setErr('');
  };

  const save = async () => {
    if (!form.full_name||!form.phone||!form.district) { setErr('Fill all required fields.'); return; }
    setSaving(true); setErr('');
    try {
      const { data: customer } = await customersApi.create({ ...form, email: form.email || null });
      // If existing customer details provided, record the installation
      if (isExisting && existingForm.product_name && existingForm.sale_date) {
        await installationsApi.create(customer.id, {
          product_name:      existingForm.product_name,
          product_id:        existingForm.product_id || null,
          installation_date: existingForm.sale_date,
          warranty_months:   existingForm.warranty_months || 12,
          installed_by:      existingForm.technician || null,
        });
      }
      setRows(r => [customer, ...r]);
      show(`Customer ${customer.customer_code} created!`);
      setAdd(false); resetModal();
    } catch(e) {
      const d = e.response?.data?.detail;
      setErr(Array.isArray(d) ? d.map(x=>x.msg).join(', ') : (d||'Failed to create customer.'));
    } finally { setSaving(false); }
  };

  const doDelete = async () => {
    try { await customersApi.delete(del.id); setRows(r=>r.filter(x=>x.id!==del.id)); show('Deleted.'); }
    catch { show('Delete failed.','error'); } finally { setDel(null); }
  };

  return (
    <div>
      <Flash/>
      <div className="search-row">
        <input placeholder="Search by name, Customer ID, phone, email…" value={q} onChange={e=>setQ(e.target.value)} onKeyDown={e=>e.key==='Enter'&&load(q)}/>
        <button className="btn" onClick={()=>load(q)}>Search</button>
        {canWrite && <button className="btn btn-primary" style={{marginLeft:'auto'}} onClick={()=>setAdd(true)}>+ Add Customer</button>}
      </div>
      <div className="card card-0">
        {busy ? <Spin/> : rows.length===0 ? <Empty msg="No customers found"/> : (
          <div className="tbl-wrap"><table>
            <thead><tr><th>Customer ID</th><th>Name</th><th>Phone</th><th>District</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {rows.map(c=>(
                <tr key={c.id} style={{cursor:'pointer'}} onClick={()=>navigate(`/customers/${c.id}`)}>
                  <td><CID code={c.customer_code}/></td>
                  <td style={{fontWeight:500}}>{c.salutation ? `${c.salutation} ${c.full_name}` : c.full_name}</td>
                  <td>{c.phone}</td>
                  <td>{c.district}</td>
                  <td><Badge label={c.status}/></td>
                  <td onClick={e=>e.stopPropagation()} style={{display:'flex',gap:4}}>
                    <button className="btn btn-sm" onClick={()=>navigate(`/customers/${c.id}`)}>View</button>
                    {canDelete && <button className="btn btn-sm btn-danger" onClick={()=>setDel(c)} title="Delete"><Trash s={13}/></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>

      <Modal open={showAdd} onClose={() => { setAdd(false); resetModal(); }} title="Add New Customer"
        footer={<><button className="btn" onClick={() => { setAdd(false); resetModal(); }}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Creating…':'Create Customer'}</button></>}
      >
        {err && <div className="notice n-err" style={{marginBottom:10}}>{err}</div>}
        <div className="notice n-info">A unique <strong>Customer ID</strong> will be auto-generated on save.</div>
        <div className="fg-2">
          <F label="Salutation"><select value={form.salutation} onChange={set('salutation')}>
            <option value="">—</option>
            {['Mr','Ms','Dr'].map(s=><option key={s}>{s}</option>)}
          </select></F>
          <F label="Full Name" req><input value={form.full_name} onChange={set('full_name')} placeholder="Customer full name"/></F>
          <F label="Address" req full><textarea value={form.address} onChange={set('address')} placeholder="Door No, Street, Area" style={{minHeight:55}}/></F>
          <F label="State" req><select value={form.state} onChange={e => setForm(f=>({...f, state:e.target.value, district:getDistricts(e.target.value)[0]||''}))}>
            {STATES.map(s=><option key={s}>{s}</option>)}
          </select></F>
          <F label="District" req><select value={form.district} onChange={set('district')}>
            {getDistricts(form.state).map(d=><option key={d}>{d}</option>)}
          </select></F>
          <F label="Pincode"><input value={form.pincode} onChange={set('pincode')} placeholder="6-digit pincode" maxLength={6}/></F>
          <F label="Mobile Number" req><input value={form.phone} onChange={set('phone')} placeholder="10-digit mobile"/></F>
          <F label="Email ID" full><input type="email" value={form.email} onChange={set('email')} placeholder="email@example.com"/></F>

          {/* Existing customer section */}
          <div style={{ gridColumn:'1/-1', borderTop:'1px solid var(--border)', paddingTop:12, marginTop:4 }}>
            <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:13, fontWeight:600 }}>
              <input type="checkbox" checked={isExisting} onChange={e => setIsExisting(e.target.checked)}/>
              This is an existing customer (already has a product)
            </label>
          </div>

          {isExisting && (<>
            <F label="Pick from Inventory" full>
              <select value={existingForm.product_id} onChange={handleProdSelect}>
                <option value="">— Select product or type below —</option>
                {prods.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </F>
            <F label="Product Name" full>
              <input value={existingForm.product_name} onChange={setEF('product_name')} placeholder="e.g. Luminous 3.5kVA Inverter"/>
            </F>
            <F label="Sale / Install Date">
              <input type="date" value={existingForm.sale_date} onChange={setEF('sale_date')}/>
            </F>
            <F label="Technician">
              <select value={existingForm.technician} onChange={setEF('technician')}>
                <option value="">— Select —</option>
                {staff.filter(s => s.is_active).map(s => <option key={s.id} value={s.id}>{s.name} ({s.role})</option>)}
              </select>
            </F>
          </>)}
        </div>
      </Modal>
      <Confirm open={!!del} onClose={()=>setDel(null)} onOk={doDelete} msg={`Delete customer ${del?.full_name}? This cannot be undone.`}/>
    </div>
  );
}
